import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostCard } from "@/components/PostCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Award, Download, Pencil, BookOpen, Quote, Search, BookText, ListChecks, Trophy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const roleTr = { admin: "Admin", moderator: "Moderatör", member: "Üye" };

function GoalRing({ percent }) {
  const r = 46, c = 2 * Math.PI * r, off = c - (percent / 100) * c;
  return (
    <svg viewBox="0 0 110 110" className="w-32 h-32 -rotate-90" data-testid="goal-ring">
      <circle cx="55" cy="55" r={r} fill="none" stroke="currentColor" className="text-surface-alt" strokeWidth="9" />
      <circle cx="55" cy="55" r={r} fill="none" stroke="currentColor" className="text-terra" strokeWidth="9"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="55" y="52" transform="rotate(90 55 55)" textAnchor="middle" className="fill-ink font-serif" style={{ fontSize: 22 }}>%{percent}</text>
    </svg>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [ap, setAp] = useState(null);
  const [badges, setBadges] = useState([]);
  const [tab, setTab] = useState("vitrin");
  const [lib, setLib] = useState([]);
  const [libStatus, setLibStatus] = useState("Okudum");
  const [posts, setPosts] = useState([]);
  const [postTur, setPostTur] = useState("");
  const [myGlossary, setMyGlossary] = useState([]);
  const [myLists, setMyLists] = useState([]);
  const [pastQ, setPastQ] = useState("");
  const [pastRes, setPastRes] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [pastSel, setPastSel] = useState("");
  const [form, setForm] = useState({ ad_soyad: "", biyografi: "", yillik_hedef: 12, profil_resmi: "", profil_kapak: "", favori_alinti: "", favori_alinti_kaynak: "", kutuphane_gizli: false, dm_sadece_takip: false });
  const [open, setOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [followTab, setFollowTab] = useState("takipci");
  const [followList, setFollowList] = useState([]);
  const [counts, setCounts] = useState({ takipci: 0, takip: 0 });

  const refresh = () => {
    api.get("/profile/stats").then((r) => setStats(r.data));
    api.get("/stats/me").then((r) => setAp(r.data));
    api.get("/badges/me").then((r) => setBadges(r.data.filter((b) => b.kazanildi)));
    if (user?.id) {
      api.get(`/users/${user.id}/followers`).then((r) => setCounts((c) => ({ ...c, takipci: r.data.length }))).catch(() => {});
      api.get(`/users/${user.id}/following`).then((r) => setCounts((c) => ({ ...c, takip: r.data.length }))).catch(() => {});
    }
  };

  const openFollow = (t) => {
    setFollowTab(t); setFollowOpen(true);
    api.get(`/users/${user.id}/${t === "takipci" ? "followers" : "following"}`).then((r) => setFollowList(r.data));
  };
  const toggleFollow = async (u) => {
    if (u.takip_ediliyor) await api.delete(`/users/${u.id}/follow`); else await api.post(`/users/${u.id}/follow`);
    setFollowList(followList.map((x) => x.id === u.id ? { ...x, takip_ediliyor: !u.takip_ediliyor } : x));
    refresh();
  };

  useEffect(() => {
    refresh();
    api.get("/books").then((r) => setAllBooks(r.data)).catch(() => {});
    if (user) setForm({
      ad_soyad: user.ad_soyad || "", biyografi: user.biyografi || "", yillik_hedef: user.yillik_hedef || 12,
      profil_resmi: user.profil_resmi || "", profil_kapak: user.profil_kapak || "",
      favori_alinti: user.favori_alinti || "", favori_alinti_kaynak: user.favori_alinti_kaynak || "",
      kutuphane_gizli: !!user.kutuphane_gizli, dm_sadece_takip: !!user.dm_sadece_takip,
    });
  }, [user]);

  useEffect(() => {
    if (tab === "kutuphane") api.get("/user-books").then((r) => setLib(r.data));
    if (tab === "paylasim") api.get("/me/posts", { params: postTur ? { tur: postTur } : {} }).then((r) => setPosts(r.data));
    if (tab === "sozluk") api.get("/me/glossary").then((r) => setMyGlossary(r.data));
    if (tab === "listeler") api.get("/me/reading-lists").then((r) => setMyLists(r.data));
  }, [tab, postTur]);

  useEffect(() => {
    if (pastQ.trim().length < 2) { setPastRes([]); return; }
    const t = setTimeout(() => api.get("/search", { params: { q: pastQ, limit: 20 } }).then((r) => setPastRes(r.data.kitaplar)), 250);
    return () => clearTimeout(t);
  }, [pastQ]);

  const addPast = async (bookId, title) => {
    await api.put("/user-books", { book_id: bookId, durum: "Okudum", gecmis: true });
    setPastQ(""); setPastRes([]); toast.success(`"${title}" geçmiş okumalara eklendi.`); refresh();
    if (tab === "kutuphane") api.get("/user-books").then((r) => setLib(r.data));
  };

  const save = async () => {
    try {
      const { data } = await api.put("/profile", { ...form, yillik_hedef: Number(form.yillik_hedef) });
      setUser(data); setOpen(false); toast.success("Profil güncellendi."); refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadNotes = () => {
    const token = localStorage.getItem("ao_token");
    fetch(`${API}/me/notes/export?format=txt`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob()).then((blob) => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "gizli_notlarim.txt"; a.click();
      });
  };

  const download = (fmt) => {    const token = localStorage.getItem("ao_token");
    const url = `${API}/profile/export?format=${fmt}`;
    if (fmt === "json") { window.open(url, "_blank"); return; }
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob()).then((blob) => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "aktifokur_verilerim.csv"; a.click();
      });
  };

  const goTab = (t, extra) => { if (extra?.libStatus) setLibStatus(extra.libStatus); if (extra?.postTur !== undefined) setPostTur(extra.postTur); setTab(t); };

  const statBoxes = [
    { label: "Okunmuş Kitap", value: stats?.okunan, icon: BookOpen, onClick: () => goTab("kutuphane", { libStatus: "Okudum" }), cls: "bg-sage text-white" },
    { label: "Okunuyor", value: stats?.okunuyor, icon: BookOpen, onClick: () => goTab("kutuphane", { libStatus: "Okuyorum" }) },
    { label: "Okunacak", value: stats?.okunacak, icon: BookOpen, onClick: () => goTab("kutuphane", { libStatus: "Okuyacağım" }) },
    { label: "Alıntı", value: stats?.alinti_sayisi, icon: Quote, onClick: () => goTab("paylasim", { postTur: "Alıntı" }) },
  ];

  const libFiltered = lib.filter((x) => x.durum === libStatus);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Kart başlık: kapak + avatar */}
      <div className="border border-line rounded-2xl overflow-hidden bg-surface fade-up">
        <div className="px-6 pt-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-32 h-32 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden shrink-0" data-testid="profile-avatar">
              {user?.profil_resmi ? <img src={user.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-6xl text-terra">{(user?.ad_soyad || "?")[0]}</span>}
            </div>
            <div className="flex-1">
              <h1 className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">{user?.ad_soyad}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{user?.email} · {roleTr[user?.role] || user?.role}</p>
              <p className="text-ink-2 mt-1 text-sm">{user?.biyografi || "Henüz biyografi eklenmemiş."}</p>
              <div className="flex gap-4 mt-2">
                <button onClick={() => openFollow("takipci")} data-testid="followers-count" className="text-sm text-ink-2 hover:text-terra">
                  <span className="font-serif text-lg text-ink">{counts.takipci}</span> Takipçi
                </button>
                <button onClick={() => openFollow("takip")} data-testid="following-count" className="text-sm text-ink-2 hover:text-terra">
                  <span className="font-serif text-lg text-ink">{counts.takip}</span> Takip
                </button>
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-profile" className="border-line gap-1"><Pencil size={15} /> Kartı Düzenle</Button>
              </DialogTrigger>
              <DialogContent className="bg-surface max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Okur Kartvizitini Düzenle</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Ad Soyad</Label><Input data-testid="edit-name" value={form.ad_soyad} onChange={(e) => setForm({ ...form, ad_soyad: e.target.value })} className="mt-1" /></div>
                  <div><Label>Biyografi</Label><Textarea data-testid="edit-bio" value={form.biyografi} onChange={(e) => setForm({ ...form, biyografi: e.target.value })} className="mt-1" /></div>
                  <div><Label>Favori Alıntın</Label><Textarea data-testid="edit-fav-quote" value={form.favori_alinti} onChange={(e) => setForm({ ...form, favori_alinti: e.target.value })} className="mt-1" placeholder="Vitrininde öne çıkacak alıntı..." /></div>
                  <div><Label>Alıntı Kaynağı</Label><Input data-testid="edit-fav-source" value={form.favori_alinti_kaynak} onChange={(e) => setForm({ ...form, favori_alinti_kaynak: e.target.value })} className="mt-1" placeholder="Yazar / kitap" /></div>
                  <div><Label>Profil Resmi URL</Label><Input data-testid="edit-avatar" value={form.profil_resmi} onChange={(e) => setForm({ ...form, profil_resmi: e.target.value })} className="mt-1" placeholder="https://... (kare/kare-yakını 200x200 önerilir)" /></div>
                  <div><Label>Yıllık Okuma Hedefi</Label><Input data-testid="edit-goal" type="number" min={1} value={form.yillik_hedef} onChange={(e) => setForm({ ...form, yillik_hedef: e.target.value })} className="mt-1" /></div>
                  <div className="flex items-center justify-between pt-2"><Label>Kütüphanem gizli olsun</Label><Switch checked={form.kutuphane_gizli} onCheckedChange={(v) => setForm({ ...form, kutuphane_gizli: v })} data-testid="toggle-library-private" /></div>
                  <div className="flex items-center justify-between"><Label>Sadece takip ettiklerim mesaj atabilsin</Label><Switch checked={form.dm_sadece_takip} onCheckedChange={(v) => setForm({ ...form, dm_sadece_takip: v })} data-testid="toggle-dm-private" /></div>
                </div>
                <DialogFooter><Button onClick={save} data-testid="save-profile" className="bg-terra hover:bg-terra-hover text-white">Kaydet</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* AP bar */}
          {ap && (
            <div className="mt-4 p-4 rounded-xl bg-ink text-page flex items-center gap-3" data-testid="profile-ap">
              <Trophy className="text-gold shrink-0" size={20} />
              <div className="flex-1">
                <div className="flex justify-between text-sm"><span>Seviye {ap.level}: <span className="text-gold">{ap.level_name}</span></span><span className="text-page/70">{ap.total_ap} AP{ap.streak ? ` · 🔥 ${ap.streak}` : ""}</span></div>
                <div className="h-1.5 mt-2 bg-page/20 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${ap.progress}%` }} /></div>
              </div>
            </div>
          )}

          {/* Tıklanabilir istatistik kutuları */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {statBoxes.map((b, i) => (
              <button key={i} onClick={b.onClick} data-testid={`profile-stat-${b.label}`}
                className={`border border-line rounded-xl p-4 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md ${b.cls || "bg-surface"}`}>
                <b.icon size={18} className={b.cls ? "text-white/80" : "text-terra"} />
                <p className="font-serif text-3xl mt-2">{b.value ?? "—"}</p>
                <p className={`text-xs ${b.cls ? "text-white/80" : "text-muted-foreground"}`}>{b.label} →</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sekmeler */}
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-surface-alt flex-wrap h-auto">
          <TabsTrigger value="vitrin" data-testid="tab-vitrin">Vitrin</TabsTrigger>
          <TabsTrigger value="kutuphane" data-testid="tab-kutuphane">Kütüphanem</TabsTrigger>
          <TabsTrigger value="paylasim" data-testid="tab-paylasim">Paylaşımlar</TabsTrigger>
          <TabsTrigger value="sozluk" data-testid="tab-sozluk">Sözlük Katkıları</TabsTrigger>
          <TabsTrigger value="listeler" data-testid="tab-listeler">Okuma Listeleri</TabsTrigger>
        </TabsList>

        {/* VİTRİN */}
        <TabsContent value="vitrin" className="space-y-6 mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="border border-line rounded-xl p-6 bg-surface flex items-center gap-5">
              <GoalRing percent={stats?.hedef_yuzde || 0} />
              <div>
                <p className="text-xs uppercase tracking-wider text-terra font-bold">Yıllık Hedef</p>
                <p className="font-serif text-2xl text-ink mt-1">{stats?.hedef_ilerleme}/{stats?.yillik_hedef}</p>
                <p className="text-sm text-muted-foreground">kitap okundu</p>
              </div>
            </div>
            <div className="border-l-4 border-gold rounded-xl p-6 bg-surface">
              <p className="text-xs uppercase tracking-wider text-gold font-bold flex items-center gap-1"><Quote size={13} /> Favori Alıntım</p>
              {user?.favori_alinti ? (
                <>
                  <p className="font-serif text-xl italic text-ink mt-2 leading-snug">"{user.favori_alinti}"</p>
                  {user.favori_alinti_kaynak && <p className="text-sm text-muted-foreground mt-2">— {user.favori_alinti_kaynak}</p>}
                </>
              ) : <p className="text-sm text-muted-foreground mt-2">Kartını düzenleyerek favori alıntını ekle.</p>}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-4 flex items-center gap-2"><Award className="text-gold" size={22} /> Rozetler</h2>
            {badges.length ? (
              <div className="flex flex-wrap gap-4" data-testid="badges">
                {badges.map((b) => (
                  <Link to="/rozetler" key={b.kod} className="flex flex-col items-center gap-2 w-24 text-center">
                    <div className="w-20 h-20 rounded-full bg-gold/10 border-2 border-gold/30 flex items-center justify-center text-3xl">🏅</div>
                    <span className="text-sm text-ink-2">{b.ad}</span>
                  </Link>
                ))}
              </div>
            ) : <p className="text-muted-foreground text-sm">Henüz rozet kazanmadın. Okumaya ve paylaşmaya başla!</p>}
          </div>

          {/* Geçmişte Okuduklarım — arama çubuğu */}
          <div className="border border-line rounded-xl p-6 bg-surface">
            <h2 className="font-serif text-2xl text-ink">Geçmişte Okuduklarım</h2>
            <p className="text-sm text-ink-2 mt-1">Daha önce okuduğun kitabı ara ve seç ya da listeden seç — bunlar yıllık hedefe sayılmaz.</p>
            <div className="relative mt-4 max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input data-testid="past-search" value={pastQ} onChange={(e) => setPastQ(e.target.value)} placeholder="Kitap adı veya yazar yaz..." className="pl-9 h-11" />
              {pastRes.length > 0 && (
                <div className="absolute top-12 left-0 right-0 bg-surface border border-line rounded-lg shadow-xl z-20 overflow-y-auto max-h-72" data-testid="past-results">
                  {pastRes.map((b) => (
                    <button key={b.id} data-testid={`past-add-${b.id}`} onClick={() => addPast(b.id, b.baslik)} className="w-full text-left px-3 py-2 hover:bg-surface-alt text-ink text-sm">
                      📖 {b.baslik} <span className="text-muted-foreground">— {b.yazar}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-3 max-w-lg">
              <select data-testid="past-select" value={pastSel} onChange={(e) => setPastSel(e.target.value)}
                className="flex-1 min-w-0 w-full h-11 rounded-md border border-line bg-surface px-3 text-sm text-ink truncate">
                <option value="">Listeden kitap seç...</option>
                {allBooks.map((b) => <option key={b.id} value={b.id}>{b.baslik.length > 45 ? b.baslik.slice(0, 45) + "…" : b.baslik} — {b.yazar}</option>)}
              </select>
              <Button data-testid="past-select-add" disabled={!pastSel} onClick={() => { const b = allBooks.find((x) => x.id === pastSel); addPast(pastSel, b?.baslik || ""); setPastSel(""); }} className="bg-ink hover:bg-ink-2 text-page h-11 shrink-0">Ekle</Button>
            </div>
          </div>

          {/* Export */}
          <div className="border border-line rounded-xl p-6 bg-surface-alt/50">
            <h2 className="font-serif text-2xl text-ink">Kişisel Verilerini İndir</h2>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => download("csv")} data-testid="export-csv" className="bg-sage hover:bg-sage/90 text-white gap-1"><Download size={15} /> Excel/CSV</Button>
              <Button onClick={() => download("json")} data-testid="export-json" variant="outline" className="border-line gap-1"><Download size={15} /> JSON</Button>
              <Button onClick={downloadNotes} data-testid="export-notes" variant="outline" className="border-line gap-1"><Download size={15} /> Gizli Notlarım (TXT)</Button>
            </div>
          </div>
        </TabsContent>

        {/* KÜTÜPHANEM */}
        <TabsContent value="kutuphane" className="mt-6">
          <div className="flex gap-2 mb-4">
            {["Okudum", "Okuyorum", "Okuyacağım"].map((s) => (
              <button key={s} onClick={() => setLibStatus(s)} data-testid={`lib-filter-${s}`}
                className={`text-sm px-3.5 py-1.5 rounded-full border ${libStatus === s ? "bg-terra text-white border-terra" : "border-line text-ink-2 hover:border-terra/40"}`}>{s}</button>
            ))}
          </div>
          <div className="space-y-2" data-testid="library-list">
            {libFiltered.length === 0 && <p className="text-sm text-muted-foreground">Bu durumda kitap yok.</p>}
            {libFiltered.map((ub) => (
              <div key={ub.id} className="flex items-center justify-between border border-line rounded-md p-3 bg-surface hover:bg-surface-alt">
                <Link to={`/kitap/${ub.book_id}`} className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">{ub.book?.baslik}</p><p className="text-xs text-muted-foreground truncate">{ub.book?.yazar}</p>
                </Link>
                <div className="flex items-center gap-3 shrink-0 pl-2">
                  {ub.puan ? <span className="text-xs text-gold">★ {ub.puan}</span> : null}
                  <button data-testid={`lib-remove-${ub.book_id}`} title="İşaretlemeyi kaldır"
                    onClick={async () => { await api.delete(`/user-books/${ub.book_id}`); toast.success("İşaretleme kaldırıldı."); api.get("/user-books").then((r) => setLib(r.data)); refresh(); }}
                    className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* PAYLAŞIMLAR */}
        <TabsContent value="paylasim" className="mt-6">
          <div className="flex gap-2 mb-4">
            {[["", "Tümü"], ["Alıntı", "Alıntılar"], ["Yorum", "İncelemeler"], ["Abstract", "Abstract"]].map(([v, l]) => (
              <button key={l} onClick={() => setPostTur(v)} data-testid={`post-filter-${l}`}
                className={`text-sm px-3.5 py-1.5 rounded-full border ${postTur === v ? "bg-terra text-white border-terra" : "border-line text-ink-2 hover:border-terra/40"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-4" data-testid="my-posts">
            {posts.length === 0 && <p className="text-sm text-muted-foreground">Henüz paylaşım yok.</p>}
            {posts.map((p) => (
              <div key={p.id}>
                {p.kitap_baslik && <Link to={`/kitap/${p.book_id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-terra mb-1.5"><BookOpen size={12} /> {p.kitap_baslik}</Link>}
                <PostCard post={p} onDelete={() => {}} />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SÖZLÜK KATKILARI */}
        <TabsContent value="sozluk" className="mt-6">
          <div className="space-y-3" data-testid="my-glossary">
            {myGlossary.length === 0 && <p className="text-sm text-muted-foreground">Henüz sözlük katkın yok.</p>}
            {myGlossary.map((g) => (
              <div key={g.id} className="border border-line rounded-lg p-4 bg-surface">
                <h3 className="font-serif text-xl text-terra flex items-center gap-2"><BookText size={16} className="text-sage" /> {g.kavram}</h3>
                <p className="text-sm text-ink-2 mt-1 leading-relaxed">{g.tanim}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* OKUMA LİSTELERİ */}
        <TabsContent value="listeler" className="mt-6">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="my-lists">
            {myLists.length === 0 && <p className="text-sm text-muted-foreground">Henüz oluşturduğun okuma listesi yok.</p>}
            {myLists.map((l) => (
              <Link key={l.id} to={`/liste/${l.id}`} className="border border-line rounded-xl p-4 bg-surface hover:shadow-md transition-shadow">
                <p className="text-xs uppercase tracking-wider text-gold font-bold flex items-center gap-1"><ListChecks size={13} /> {l.tur}</p>
                <h3 className="font-serif text-lg text-ink mt-1">{l.liste_adi}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.aciklama}</p>
                <p className="text-xs text-terra mt-2">{l.kitaplar?.length || 0} kitap</p>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={followOpen} onOpenChange={setFollowOpen}>
        <DialogContent className="bg-surface max-w-md max-h-[80vh] overflow-y-auto" data-testid="follow-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{followTab === "takipci" ? "Takipçiler" : "Takip Edilenler"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {followList.length === 0 && <p className="text-sm text-muted-foreground">Liste boş.</p>}
            {followList.map((u) => (
              <div key={u.id} className="flex items-center gap-3 border border-line rounded-lg p-2.5">
                <Link to={`/uye/${u.id}`} onClick={() => setFollowOpen(false)} data-testid={`follow-user-${u.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-terra/10 flex items-center justify-center overflow-hidden shrink-0">
                    {u.profil_resmi ? <img src={u.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="text-terra font-serif">{(u.ad_soyad || "?")[0]}</span>}
                  </div>
                  <span className="text-sm font-medium text-ink hover:text-terra truncate">{u.ad_soyad}</span>
                </Link>
                {!u.is_self && (
                  <button onClick={() => toggleFollow(u)} data-testid={`follow-toggle-${u.id}`}
                    className={`text-xs px-2.5 py-1 rounded-full border shrink-0 transition-colors ${u.takip_ediliyor ? "border-line text-muted-foreground hover:text-destructive" : "border-terra text-terra hover:bg-terra hover:text-white"}`}>
                    {u.takip_ediliyor ? "Takiptesin ✓" : "+ Takip Et"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
